import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X, Package } from "lucide-react";
import { EmptyState, PriceTag, Skeleton } from "@aurelia/ui";
import { useCart } from "../../features/cart/queries";
import { useUpdateCartItem, useRemoveCartItem, useRemoveBundleFromCart } from "../../features/cart/mutations";
import { useDocumentMeta } from "../../lib/useDocumentMeta";

function CartQuantityInput({
  quantity,
  stock,
  onCommit
}: {
  quantity: number;
  stock: number;
  onCommit: (next: number) => void;
}) {
  const [value, setValue] = useState(String(quantity));

  useEffect(() => {
    setValue(String(quantity));
  }, [quantity]);

  const commit = () => {
    const parsed = Math.floor(Number(value));
    const clamped = Number.isFinite(parsed) ? Math.min(Math.max(parsed, 1), stock) : quantity;
    setValue(String(clamped));
    if (clamped !== quantity) onCommit(clamped);
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      aria-label="Quantity"
      value={value}
      onChange={(e) => setValue(e.target.value.replace(/[^0-9]/g, ""))}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
      }}
      className="w-9 text-center text-sm bg-transparent focus:outline-none"
    />
  );
}

export function CartPage() {
  useDocumentMeta("Your Cart", "Review your cart before checkout.", undefined, undefined, { noindex: true });
  const { data: cart, isLoading, isError } = useCart();
  const updateItem = useUpdateCartItem();
  const removeItem = useRemoveCartItem();
  const removeBundle = useRemoveBundleFromCart();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <main className="max-w-3xl mx-auto px-6 py-16 flex flex-col gap-4">
        <h1 className="text-display-sm mb-6">Your cart</h1>
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-28" />
        ))}
      </main>
    );
  }

  if (isError) {
    return (
      <main className="max-w-3xl mx-auto px-6 py-24">
        <EmptyState
          tone="error"
          title="Couldn't load your cart"
          description="Please refresh and try again."
        />
      </main>
    );
  }

  if (!cart || (cart.items.length === 0 && cart.bundleLines.length === 0)) {
    return (
      <main className="max-w-3xl mx-auto px-6 py-24 text-center flex flex-col items-center gap-4">
        <h1 className="text-display-sm">Your cart is empty</h1>
        <p className="text-ink-muted">Browse the collection and find something you'll love.</p>
        <Link
          to="/"
          className="mt-2 h-12 px-6 rounded-full bg-ink text-canvas flex items-center font-medium hover:bg-accent hover:text-ink transition-colors duration-300 dark:bg-accent dark:text-ink"
        >
          Continue shopping
        </Link>
      </main>
    );
  }

  return (
    <main className="max-w-3xl mx-auto px-6 py-16">
      <h1 className="text-display-sm mb-10">Your cart</h1>
      <div className="flex flex-col gap-4">
        {cart.bundleLines.map((line) => (
          <div
            key={line.bundleId}
            className="flex items-center gap-4 rounded-2xl border border-accent/40 bg-accent-soft/40 p-4"
          >
            <div className="w-20 h-20 rounded-xl bg-surface overflow-hidden shrink-0 flex items-center justify-center">
              {line.imageUrl ? (
                <img src={line.imageUrl} alt={line.bundleName} className="w-full h-full object-cover" />
              ) : (
                <Package size={24} className="text-ink-muted" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <Link to={`/bundles/${line.bundleSlug}`} className="font-medium truncate hover:underline block">
                {line.bundleName}
              </Link>
              <p className="text-sm text-ink-muted truncate">
                {line.items.map((i) => `${i.quantity} × ${i.productName}`).join(", ")}
              </p>
              <PriceTag cents={line.unitPrice} className="text-sm mt-1" />
            </div>
            {line.quantity > 1 && (
              <span className="text-sm text-ink-muted shrink-0">× {line.quantity}</span>
            )}
            <button
              type="button"
              aria-label="Remove bundle"
              disabled={removeBundle.isPending}
              className="h-9 w-9 flex items-center justify-center text-ink-muted hover:text-red-500 transition-colors disabled:opacity-40"
              onClick={() => removeBundle.mutate(line.bundleId)}
            >
              <X size={16} />
            </button>
          </div>
        ))}
        {cart.items.map((item) => (
          <div key={item.id} className="flex gap-4 rounded-2xl border border-border bg-surface-raised p-4">
            <div className="w-20 h-20 rounded-xl bg-surface overflow-hidden shrink-0">
              {item.imageUrl && <img src={item.imageUrl} alt={item.productName} className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{item.productName}</p>
                <p className="text-sm text-ink-muted">
                  {[item.size, item.color].filter(Boolean).join(" · ") || "Standard"}
                </p>
                <PriceTag cents={item.unitPrice} className="text-sm mt-1" />
              </div>
              <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4">
                <div className="flex items-center gap-2 border border-border rounded-full shrink-0">
                  <button
                    type="button"
                    aria-label="Decrease quantity"
                    className="h-9 w-9 flex items-center justify-center text-ink-muted hover:text-ink"
                    onClick={() => updateItem.mutate({ itemId: item.id, quantity: Math.max(1, item.quantity - 1) })}
                  >
                    <Minus size={14} />
                  </button>
                  <CartQuantityInput
                    quantity={item.quantity}
                    stock={item.stock}
                    onCommit={(next) => updateItem.mutate({ itemId: item.id, quantity: next })}
                  />
                  <button
                    type="button"
                    aria-label="Increase quantity"
                    className="h-9 w-9 flex items-center justify-center text-ink-muted hover:text-ink"
                    disabled={item.quantity >= item.stock}
                    onClick={() => updateItem.mutate({ itemId: item.id, quantity: item.quantity + 1 })}
                  >
                    <Plus size={14} />
                  </button>
                </div>
                <button
                  type="button"
                  aria-label="Remove item"
                  className="h-9 w-9 flex items-center justify-center text-ink-muted hover:text-red-500 transition-colors shrink-0"
                  onClick={() => removeItem.mutate(item.id)}
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10 flex flex-col gap-2 border-t border-border pt-6">
        <div className="flex items-center justify-between">
          <span className="text-ink-muted">Subtotal</span>
          <PriceTag cents={cart.subtotal} />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-ink-muted">Shipping</span>
          {cart.shippingFee === null ? (
            <span className="text-sm text-ink-muted">Calculated at checkout</span>
          ) : (
            <PriceTag cents={cart.shippingFee} />
          )}
        </div>
        <div className="flex items-center justify-between font-medium">
          <span>Total{cart.shippingFee === null ? " (+ shipping)" : ""}</span>
          <PriceTag cents={cart.subtotal + (cart.shippingFee ?? 0)} className="text-xl" />
        </div>
      </div>

      <button
        type="button"
        onClick={() => navigate("/checkout")}
        className="mt-6 w-full h-14 rounded-full bg-ink text-canvas font-medium tracking-tight hover:bg-accent hover:text-ink transition-colors duration-300 ease-physical dark:bg-accent dark:text-ink"
      >
        Proceed to checkout
      </button>
    </main>
  );
}
